import React from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import { YStack, XStack, Separator, useMedia } from 'tamagui'

import { GuildCard } from '@newComponents/Guild/GuildCard'
import { Grid } from '@newComponents/Grid'
import { Button } from '@newComponents/Button/Button'
import { H2 } from '@newComponents/Text'

import { GuildNetworksSection_guild$key } from './__generated__/GuildNetworksSection_guild.graphql'

const INITIAL_COUNTS = {
  0: 1,
  1: 1,
  2: 2,
  3: 3,
  4: 3,
}

function useItemsPerRow() {
  const media = useMedia()
  if (media.xs) return INITIAL_COUNTS[0]
  if (media.sm) return INITIAL_COUNTS[1]
  if (media.md) return INITIAL_COUNTS[2]
  if (media.lg) return INITIAL_COUNTS[3]
  return INITIAL_COUNTS[4]
}

export const GuildNetworksSection = ({
  guild,
}: {
  guild: GuildNetworksSection_guild$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildNetworksSection_guild on Guild
      @refetchable(queryName: "GuildNetworksSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        networks: rankedNetworks(
          membershipStatus: ACCEPTED
          first: $count
          after: $cursor
        ) @connection(key: "GuildNetworksSection_guild_networks") {
          edges {
            node {
              ...GuildCardNew_guild
            }
          }
        }
      }
    `,
    guild
  )

  const itemsPerRow = useItemsPerRow()
  const media = useMedia()

  if (data.networks.edges.length === 0) {
    return null
  }

  if (media.xs) {
    return (
      <YStack gap='$4'>
        <XStack gap='$4' alignItems='center'>
          <H2 size='$4' color='$colorMuted' fontWeight='$5'>
            Guild Networks
          </H2>
          <Separator flex={1} borderColor='$borderColor' />
        </XStack>

        {data.networks.edges.map((edge, index) => (
          <GuildCard
            key={index}
            guild={edge.node}
            limitDescriptionLines={8}
          />
        ))}

        {hasNext && (
          <Button
            secondary
            onPress={() => {
              if (isLoadingNext) return
              loadNext(3)
            }}
          >
            <Button.Text secondary>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Button.Text>
          </Button>
        )}
      </YStack>
    )
  }

  const networkGroups = data.networks.edges.reduce<
    Array<Array<(typeof data.networks.edges)[number]>>
  >((memo, network, index) => {
    if (index % itemsPerRow === 0) {
      memo.push([network])
    } else {
      memo[memo.length - 1].push(network)
    }

    return memo
  }, [])

  return (
    <YStack gap='$4'>
      <XStack gap='$4' alignItems='center'>
        <H2 size='$4' color='$colorMuted' fontWeight='$5'>
          Guild Networks
        </H2>
        <Separator flex={1} borderColor='$borderColor' />
      </XStack>

      <YStack gap='$4'>
        {networkGroups.map((group, index) => {
          return (
            <XStack key={index}>
              <Grid gap='$4'>
                {group.map((edge, edgeIndex) => (
                  <Grid.Column key={edgeIndex}>
                    <GuildCard
                      guild={edge.node}
                      limitDescriptionLines={8}
                      expand
                    />
                  </Grid.Column>
                ))}

                {Array.from({
                  length: itemsPerRow - group.length,
                }).map((_, i) => (
                  <Grid.Column key={i} />
                ))}
              </Grid>
            </XStack>
          )
        })}
      </YStack>

      {hasNext && (
        <Button
          secondary
          onPress={() => {
            if (isLoadingNext) return
            loadNext(3)
          }}
        >
          <Button.Text secondary>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Button.Text>
        </Button>
      )}
    </YStack>
  )
}
